import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppSettings,
  EmployeeSummary,
  LateAnswerPolicy,
  NegativeRule,
  PageResult,
} from '@blesk/contracts';
import { apiGet, apiPut } from '../../shared/api';
import { useHasPermission } from '../../shared/session';
import { PermissionGate } from '../shell/PermissionGate';
import { ErrorNote } from '../shell/ui';
import { ObserverPicker } from '../shell/ObserverPicker';

/** §9.4, §13, §26 ТЗ и решение E1. Раздел «Правила негативной оценки». */
export function NegativeRulesPage() {
  return (
    <PermissionGate permission="surveys.read">
      <NegativeRulesInner />
    </PermissionGate>
  );
}

const OPERATOR_LABEL: Record<NegativeRule['operator'], string> = {
  lte: 'не выше',
  lt: 'ниже',
  eq: 'равна',
};

const SCOPE_LABEL: Record<NegativeRule['scope'], string> = {
  any_required_scored: 'хотя бы один оцениваемый вопрос',
  all_required_scored: 'все оцениваемые вопросы',
  average: 'средняя по оцениваемым вопросам',
};

/** E1: политика позднего ответа. Дефолт — отклонять, история не переписывается. */
const LATE_POLICY_LABEL: Record<LateAnswerPolicy, string> = {
  reject: 'Отклонять: срок истёк, ответ не принимается',
  accept_no_incident: 'Принимать, инцидент не заводить',
  accept_with_incident: 'Принимать и заводить инцидент как обычно',
};

function NegativeRulesInner() {
  const canWrite = useHasPermission('surveys.write');
  const canPickEmployees = useHasPermission('employees.read');
  const queryClient = useQueryClient();
  // Какая настройка сохранилась последней. Настройка общая на весь сервис и
  // уходит в срок задач Bitrix24 — сохранение без видимого ответа читалось как
  // «форма ничего не сделала» (QA 22.08.2026, дефект 12).
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiGet<AppSettings>('/api/settings'),
  });

  const save = useMutation({
    mutationFn: (input: { key: string; value: unknown }) =>
      apiPut(`/api/settings/${input.key}`, { value: input.value }),
    onMutate: () => setSavedKey(null),
    onSuccess: (_result, input) => {
      setSavedKey(input.key);
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  if (!settings.data) {
    return (
      <>
        <h1>Правила негативной оценки</h1>
        <ErrorNote error={settings.error} />
        {settings.isPending && <p className="muted">Загрузка…</p>}
      </>
    );
  }

  return (
    <>
      <h1>Правила негативной оценки</h1>
      <p className="muted">
        Изменение правила не переписывает уже созданные инциденты: каждый остаётся с тем правилом,
        по которому был заведён.
      </p>
      <ErrorNote error={save.error} />

      <RuleForm
        rule={settings.data.negative_rule}
        disabled={!canWrite || save.isPending}
        saved={savedKey === 'negative_rule'}
        onSave={(value) => save.mutate({ key: 'negative_rule', value })}
      />

      {/*
       * §9.4: правило копируется снимком в версию анкеты при публикации.
       * Без этой оговорки экран обещает немедленное действие, а заказчик потом
       * не понимает, почему по действующей анкете инциденты идут по-старому.
       */}
      <p className="muted">
        Новое правило начинает действовать только для версий анкет, опубликованных после сохранения:
        при публикации версия забирает правило себе и дальше работает по нему. Уже опубликованные
        версии продолжают считать негатив по своему снимку — чтобы правило подействовало на
        действующую анкету, опубликуйте её новую версию.
      </p>

      <div className="section-block">
        <h3>Единый срок реакции</h3>
        <p className="muted">
          Часы, за которые менеджер обязан отреагировать. Значение уходит в срок задачи Bitrix24.
        </p>
        <NumberSetting
          label="Срок реакции"
          value={settings.data.task_reaction_hours}
          min={1}
          max={720}
          suffix="ч"
          disabled={!canWrite || save.isPending}
          saved={savedKey === 'task_reaction_hours'}
          onSave={(value) => save.mutate({ key: 'task_reaction_hours', value })}
        />
      </div>

      {canPickEmployees && (
        <div className="section-block">
          <h3>Наблюдатели во всех задачах Bitrix24</h3>
          <p className="muted">
            Попадают наблюдателями в каждую задачу — по негативу и по обращениям любого типа. Обычно
            это руководитель производственного отдела и его заместитель. Наблюдатели конкретного
            объекта (менеджер коммерческого отдела) задаются в разделе «Объекты». Уже созданные
            задачи не меняются.
          </p>
          <ObserversSetting
            value={settings.data.task_observer_ids}
            disabled={!canWrite || save.isPending}
            saved={savedKey === 'task_observer_ids'}
            onSave={(value) => save.mutate({ key: 'task_observer_ids', value })}
          />
        </div>
      )}

      <div className="section-block">
        <h3>Поздний ответ на опрос</h3>
        <p className="muted">
          Что делать с ответом, который пришёл после того, как срок опроса истёк.
        </p>
        <LatePolicySetting
          value={settings.data.late_answer_policy}
          disabled={!canWrite || save.isPending}
          saved={savedKey === 'late_answer_policy'}
          onSave={(value) => save.mutate({ key: 'late_answer_policy', value })}
        />
      </div>

      {!canWrite && <p className="muted">Изменение правил доступно системному администратору.</p>}
    </>
  );
}

function RuleForm({
  rule,
  disabled,
  saved,
  onSave,
}: {
  rule: NegativeRule;
  disabled: boolean;
  saved: boolean;
  onSave: (value: NegativeRule) => void;
}) {
  const [draft, setDraft] = useState<NegativeRule>(rule);
  const changed = JSON.stringify(draft) !== JSON.stringify(rule);

  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(draft);
      }}
    >
      <label>
        Оценка
        <select
          value={draft.operator}
          disabled={disabled}
          onChange={(event) =>
            setDraft({ ...draft, operator: event.target.value as NegativeRule['operator'] })
          }
        >
          {Object.entries(OPERATOR_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label>
        Порог (шкала 1–5)
        <input
          type="number"
          min={1}
          max={5}
          value={draft.value}
          disabled={disabled}
          onChange={(event) => setDraft({ ...draft, value: Number(event.target.value) })}
        />
      </label>

      <label>
        Область применения
        <select
          value={draft.scope}
          disabled={disabled}
          onChange={(event) =>
            setDraft({ ...draft, scope: event.target.value as NegativeRule['scope'] })
          }
        >
          {Object.entries(SCOPE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <p className="muted">
        Инцидент заводится, когда {SCOPE_LABEL[draft.scope]} {OPERATOR_LABEL[draft.operator]}{' '}
        {draft.value}.
      </p>

      <div className="form__actions">
        <button type="submit" className="btn-primary" disabled={disabled || !changed}>
          Сохранить правило
        </button>
        <SavedMark shown={saved && !changed} />
      </div>
    </form>
  );
}

/** Подтверждение записи — как «Скопировано» у приглашений (дефект 12). */
function SavedMark({ shown }: { shown: boolean }) {
  if (!shown) return null;
  return (
    <span className="muted" role="status">
      Сохранено
    </span>
  );
}

/**
 * E1: политика позднего ответа.
 *
 * Настройка правится так же, как остальные на экране: выбор меняет черновик,
 * фиксирует кнопка. Мгновенное применение при выборе означало бы, что
 * пролистывание списка стрелками меняет правило приёма ответов без спроса.
 */
function LatePolicySetting({
  value,
  disabled,
  saved,
  onSave,
}: {
  value: LateAnswerPolicy;
  disabled: boolean;
  saved: boolean;
  onSave: (value: LateAnswerPolicy) => void;
}) {
  const [draft, setDraft] = useState<LateAnswerPolicy>(value);

  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(draft);
      }}
    >
      <label>
        Поведение
        <select
          value={draft}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value as LateAnswerPolicy)}
        >
          {Object.entries(LATE_POLICY_LABEL).map(([option, label]) => (
            <option key={option} value={option}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <div className="form__actions">
        <button type="submit" className="btn-primary" disabled={disabled || draft === value}>
          Сохранить
        </button>
        <SavedMark shown={saved && draft === value} />
      </div>
    </form>
  );
}

/** А13: общие наблюдатели задач. Сохраняется кнопкой, как остальные настройки экрана. */
function ObserversSetting({
  value,
  disabled,
  saved,
  onSave,
}: {
  value: string[];
  disabled: boolean;
  saved: boolean;
  onSave: (value: string[]) => void;
}) {
  const [draft, setDraft] = useState<string[]>(value);
  const employees = useQuery({
    queryKey: ['employees', 'options'],
    queryFn: () =>
      apiGet<PageResult<EmployeeSummary>>('/api/employees', { status: 'ACTIVE', perPage: 200 }),
    select: (result) => result.items,
  });
  const changed = draft.length !== value.length || draft.some((id) => !value.includes(id));

  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(draft);
      }}
    >
      {employees.isPending ? (
        <p className="muted">Загрузка…</p>
      ) : (
        <ObserverPicker
          employees={employees.data ?? []}
          value={draft}
          onChange={setDraft}
          disabled={disabled}
        />
      )}
      <ErrorNote error={employees.error} />
      <div className="form__actions">
        <button type="submit" className="btn-primary" disabled={disabled || !changed}>
          Сохранить
        </button>
        <SavedMark shown={saved && !changed} />
      </div>
    </form>
  );
}

function NumberSetting({
  label,
  value,
  min,
  max,
  suffix,
  disabled,
  saved,
  onSave,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  suffix: string;
  disabled: boolean;
  saved: boolean;
  onSave: (value: number) => void;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(draft);
      }}
    >
      {/* Единица измерения — в подписи поля, а не отдельной строкой под ним:
          поле занимает всю ширину формы, и «ч» после него отрывалось на
          следующую строку и читалось как обрывок, а не как единица. */}
      <label>
        {`${label} (${suffix})`}
        <input
          type="number"
          min={min}
          max={max}
          value={draft}
          disabled={disabled}
          onChange={(event) => setDraft(Number(event.target.value))}
        />
      </label>
      <div className="form__actions">
        <button type="submit" className="btn-primary" disabled={disabled || draft === value}>
          Сохранить
        </button>
        <SavedMark shown={saved && draft === value} />
      </div>
    </form>
  );
}
